import { useState } from "react";
import { BookOpen, ChevronDown, ChevronUp, Shield, AlertTriangle, CheckCircle } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";

interface OperatorInfo {
  operator: string;
  syntax: string;
  description: string;
  example: string;
  category: "basic" | "advanced" | "file" | "domain";
}

const operators: OperatorInfo[] = [
  { operator: "site:", syntax: "site:example.com", description: "Restrict results to a specific domain or website", example: "site:github.com react hooks", category: "domain" },
  { operator: "inurl:", syntax: "inurl:keyword", description: "Find pages with keyword in the URL path", example: "inurl:login admin panel", category: "basic" },
  { operator: "intitle:", syntax: "intitle:keyword", description: "Find pages with keyword in the title tag", example: "intitle:\"index of\" backup", category: "basic" },
  { operator: "intext:", syntax: "intext:keyword", description: "Find pages containing keyword in the body text", example: "intext:\"password\" filetype:txt", category: "basic" },
  { operator: "filetype:", syntax: "filetype:pdf", description: "Restrict results to specific file extensions", example: "filetype:pdf annual report 2024", category: "file" },
  { operator: "ext:", syntax: "ext:docx", description: "Alternative to filetype: for file extensions", example: "ext:xlsx budget confidential", category: "file" },
  { operator: "\"exact phrase\"", syntax: "\"exact match\"", description: "Search for an exact phrase match", example: "\"vulnerability disclosure\"", category: "basic" },
  { operator: "-exclude", syntax: "-keyword", description: "Exclude results containing this word", example: "python tutorial -beginner", category: "basic" },
  { operator: "OR", syntax: "term1 OR term2", description: "Find pages containing either term", example: "CVE-2024 OR CVE-2023 exploit", category: "advanced" },
  { operator: "cache:", syntax: "cache:url", description: "View Google's cached version of a page", example: "cache:example.com", category: "advanced" },
  { operator: "related:", syntax: "related:site.com", description: "Find sites similar to the specified URL", example: "related:github.com", category: "domain" },
  { operator: "allinurl:", syntax: "allinurl:word1 word2", description: "All words must appear in the URL", example: "allinurl:admin login portal", category: "advanced" },
  { operator: "allintitle:", syntax: "allintitle:word1 word2", description: "All words must appear in the title", example: "allintitle:security audit report", category: "advanced" },
  { operator: "before:/after:", syntax: "before:2024-01-01", description: "Find content indexed before/after a date", example: "CVE after:2024-01-01 critical", category: "advanced" },
];

const categoryColors = {
  basic: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  advanced: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  file: "bg-orange-500/10 text-orange-400 border-orange-500/30",
  domain: "bg-green-500/10 text-green-400 border-green-500/30",
};

export function OperatorsGuide() {
  const { t } = useLocale();
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const filteredOperators = selectedCategory
    ? operators.filter((op) => op.category === selectedCategory)
    : operators;

  const categoryLabels: Record<string, string> = {
    basic: t("operators.cat_basic"),
    advanced: t("operators.cat_advanced"),
    file: t("operators.cat_file"),
    domain: t("operators.cat_domain"),
  };

  return (
    <div className="glass-card p-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{t("operators.title")}</h3>
            <p className="text-xs text-muted-foreground">{t("operators.subtitle")}</p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4 animate-fade-in">
          {/* Ethical Guidelines */}
          <div className="p-4 rounded-xl bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/20">
            <div className="flex items-start gap-3 mb-3">
              <Shield className="w-5 h-5 text-yellow-400 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-400">{t("operators.ethical_title")}</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("operators.ethical_subtitle")}
                </p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-3 mt-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-foreground/80">{t("operators.allowed_1")}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-foreground/80">{t("operators.allowed_2")}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-foreground/80">{t("operators.allowed_3")}</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-foreground/80">{t("operators.blocked_1")}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-foreground/80">{t("operators.blocked_2")}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-foreground/80">{t("operators.blocked_3")}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                selectedCategory === null
                  ? "bg-primary/20 text-primary border-primary/30"
                  : "bg-muted/30 text-muted-foreground border-border/30 hover:bg-muted/50"
              }`}
            >
              {t("operators.all")} ({operators.length})
            </button>
            {(["basic", "advanced", "file", "domain"] as const).map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors capitalize ${
                  selectedCategory === cat
                    ? categoryColors[cat]
                    : "bg-muted/30 text-muted-foreground border-border/30 hover:bg-muted/50"
                }`}
              >
                {categoryLabels[cat]} ({operators.filter((o) => o.category === cat).length})
              </button>
            ))}
          </div>

          {/* Operators Grid */}
          <div className="grid gap-3 md:grid-cols-2">
            {filteredOperators.map((op) => (
              <div
                key={op.operator}
                className="p-3 rounded-lg bg-cyber-darker border border-border/30 hover:border-primary/30 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <code className="text-sm font-mono font-bold text-primary">{op.operator}</code>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${categoryColors[op.category]}`}>
                    {categoryLabels[op.category]}
                  </span>
                </div>
                <p className="text-sm text-foreground/80 mb-2">{op.description}</p>
                <div className="p-2 rounded bg-muted/30 border border-border/20">
                  <code className="text-xs font-mono text-muted-foreground break-all">{op.example}</code>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
